import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-light-svg-icons'
import { faCompressArrowsAlt, faExclamationTriangle, faExpandArrowsAlt } from '@fortawesome/pro-duotone-svg-icons'
import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns'
import moment from 'moment-timezone'

export type ScheduleCalendarViewMode = 'week' | 'month'
export type ScheduleCalendarTimezoneMode = 'warehouse' | 'viewer'

export type ScheduleCalendarEntry = {
  id: string
  sourceId: number | string
  date: string
  kind: 'scheduled' | 'blackout' | string
  title: string
  subtitle: string
  scheduledAt: string
  displayTimeLabel?: string
  orderEstimateLabel?: string
  minutesFromMidnight?: number
  statusTone: 'active' | 'inactive' | 'attention' | 'blackout' | 'success' | 'failed'
}

export type ScheduleCalendarSlot = {
  date: string
  time: string
  scheduledAt: string
}

type WeekdayOption = {
  value: number
  shortLabel: string
}

const weekdayOptions: WeekdayOption[] = [
  { value: 0, shortLabel: 'Mon' },
  { value: 1, shortLabel: 'Tue' },
  { value: 2, shortLabel: 'Wed' },
  { value: 3, shortLabel: 'Thu' },
  { value: 4, shortLabel: 'Fri' },
  { value: 5, shortLabel: 'Sat' },
  { value: 6, shortLabel: 'Sun' },
]

const HOUR_ROW_HEIGHT = 72
const WEEK_VIEW_HEIGHT = 640
const DEFAULT_EVENT_DURATION_MINUTES = 30
const DEFAULT_CREATE_SLOT_INTERVAL_MINUTES = 30
const MIN_EVENT_CARD_HEIGHT = 22
const WEEK_GRID_TOP_OFFSET = 18
const WEEK_GRID_BOTTOM_OFFSET = 0
const DEFAULT_START_HOUR = 0
const DEFAULT_END_HOUR = 24
const INITIAL_WEEK_SCROLL_HOUR = 5

const CalendarPanel = styled.div`
  border: 1px solid #dfe7f3;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 10px 28px rgba(34, 46, 84, 0.06);

  .calendar-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 1.25rem 1rem;
    border-bottom: 1px solid #dfe7f3;
    background: linear-gradient(180deg, #fbfcff 0%, #f6f8fc 100%);
  }

  .calendar-title {
    flex: 1;
    min-width: 0;
  }

  .calendar-title-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .calendar-title-top h4 {
    margin: 0;
    color: #293241;
    font-size: 1rem;
    font-weight: 800;
  }

  .calendar-title {
    p {
      margin: 0.45rem 0 0;
      color: #687386;
      font-size: 0.85rem;
      line-height: 1.45;
    }
  }

  .calendar-nav {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .calendar-controls {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
  }

  .timezone-switch {
    display: inline-flex;
    gap: 0.25rem;
    padding: 0.2rem;
    border: 1px solid #d8e0ed;
    border-radius: 7px;
    background: #eef3fa;
  }

  .timezone-switch button {
    border: 0;
    border-radius: 5px;
    padding: 0.4rem 0.7rem;
    background: transparent;
    color: #64748b;
    font-size: 0.76rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .timezone-switch button.active {
    background: #ffffff;
    color: #2457b8;
    box-shadow: 0 1px 3px rgba(34, 46, 84, 0.12);
  }

  .view-toggle {
    display: inline-flex;
    gap: 0.25rem;
    padding: 0.2rem;
    border: 1px solid #d8e0ed;
    border-radius: 7px;
    background: #eef3fa;
  }

  .view-toggle button {
    border: 0;
    border-radius: 5px;
    padding: 0.4rem 0.8rem;
    background: transparent;
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 700;
  }

  .view-toggle button.active {
    background: #ffffff;
    color: #2457b8;
    box-shadow: 0 1px 3px rgba(34, 46, 84, 0.12);
  }

  .calendar-button {
    width: 2.25rem;
    height: 2.25rem;
    border: 1px solid #d8e0ed;
    border-radius: 6px;
    background: #ffffff;
    color: #3f4b5f;
  }

  .calendar-button:hover {
    border-color: #7aa7f8;
    background: #f3f7ff;
    color: #2457b8;
  }

  .calendar-today-button {
    width: auto;
    padding: 0 0.75rem;
    font-size: 0.8rem;
    font-weight: 700;
  }

  .calendar-expand-button {
    font-size: 0.9rem;
  }

  &.fullscreen {
    position: fixed;
    inset: 1rem;
    z-index: 1060;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    overscroll-behavior: contain;
  }

  &.fullscreen .calendar-body {
    flex: 1;
    min-height: 0;
    height: 100%;
    align-items: stretch;
    overflow: hidden;
  }

  &.fullscreen .calendar-main {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  &.fullscreen .week-shell {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &.fullscreen .week-scroll {
    flex: 1;
    min-height: 0;
    height: auto;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  &.fullscreen .calendar-main > .calendar-grid {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  &.fullscreen .agenda-panel {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
  }

  .calendar-body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    align-items: start;
  }

  .calendar-main {
    min-width: 0;
  }

  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .weekday-cell {
    padding: 0.7rem 0.5rem;
    border-bottom: 1px solid #dfe7f3;
    background: #f7f9fd;
    color: #687386;
    font-size: 0.76rem;
    font-weight: 700;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .day-cell {
    min-height: 144px;
    padding: 0.55rem;
    border-right: 1px solid #edf2f8;
    border-bottom: 1px solid #edf2f8;
    background: #ffffff;
    cursor: pointer;
  }

  .day-cell.placeholder {
    cursor: default;
    background: #fbfcfe;
  }

  .day-cell.muted {
    background: #fbfcfe;
    color: #94a3b8;
  }

  .day-cell.today {
    background: #f6faff;
  }

  .day-cell.selected {
    background: #f4f8ff;
    box-shadow: inset 0 3px 0 #4078dc;
  }

  .day-number-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
  }

  .day-number {
    color: #293241;
    font-size: 0.85rem;
    font-weight: 700;
  }

  .today-badge {
    border-radius: 999px;
    padding: 0.15rem 0.45rem;
    background: #e4efff;
    color: #2457b8;
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .day-cell.muted .day-number {
    color: #94a3b8;
  }

  .entry-list {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .entry {
    width: 100%;
    border: 0;
    border: 1px solid rgba(64, 120, 220, 0.16);
    border-radius: 7px;
    padding: 0.55rem 0.6rem;
    text-align: left;
    background: #edf5ff;
    color: #2457b8;
    box-shadow: 0 3px 10px rgba(36, 87, 184, 0.08);
  }

  .entry.success {
    border-color: rgba(2, 122, 72, 0.16);
    background: #ecf8f0;
    color: #027a48;
  }

  .entry.attention {
    border-color: rgba(146, 64, 14, 0.18);
    background: #fff7df;
    color: #92400e;
  }

  .entry.failed {
    border-color: rgba(180, 35, 24, 0.22);
    background: #fff1f1;
    color: #b42318;
    box-shadow: 0 4px 12px rgba(180, 35, 24, 0.12);
  }

  .entry.blackout {
    border-color: rgba(15, 23, 42, 0.3);
    background: #e2e8f0;
    color: #0f172a;
    box-shadow: 0 3px 10px rgba(15, 23, 42, 0.1);
  }

  .entry-title {
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .entry-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .entry-time {
    flex: 0 0 auto;
    border-radius: 999px;
    padding: 0.14rem 0.42rem;
    background: rgba(255, 255, 255, 0.75);
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    line-height: 1.2;
    white-space: nowrap;
  }

  .entry-warning {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    margin-top: 0.34rem;
    border-radius: 999px;
    padding: 0.16rem 0.42rem;
    background: rgba(180, 35, 24, 0.1);
    color: #b42318;
    font-size: 0.66rem;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
  }

  .entry-subtitle {
    display: block;
    margin-top: 0.28rem;
    font-size: 0.72rem;
    line-height: 1.25;
    opacity: 0.92;
  }

  .entry-more {
    color: #64748b;
    font-size: 0.72rem;
    font-weight: 600;
  }

  .week-shell {
    border-top: 1px solid #dfe7f3;
  }

  .week-header-row {
    display: grid;
    grid-template-columns: 80px repeat(7, minmax(0, 1fr));
    position: sticky;
    top: 0;
    z-index: 12;
    border-bottom: 1px solid #dfe7f3;
    background: #f7f9fd;
  }

  .week-corner {
    position: relative;
    z-index: 12;
    border-right: 1px solid #dfe7f3;
    background: #f7f9fd;
  }

  .week-header-cell {
    position: relative;
    z-index: 12;
    padding: 0.8rem 0.5rem;
    border-right: 1px solid #dfe7f3;
    text-align: center;
    cursor: pointer;
    background: #f7f9fd;
  }

  .week-header-cell:last-child {
    border-right: none;
  }

  .week-header-cell.today {
    background: #eef5ff;
  }

  .week-header-cell.selected {
    background: #f4f8ff;
    box-shadow: inset 0 -2px 0 #4078dc;
  }

  .week-header-label {
    display: block;
    color: #687386;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .week-header-date {
    display: block;
    margin-top: 0.22rem;
    color: #293241;
    font-size: 0.95rem;
    font-weight: 700;
  }

  .week-scroll {
    height: ${WEEK_VIEW_HEIGHT}px;
    overflow-y: scroll;
    overflow-x: hidden;
  }

  .week-grid {
    display: grid;
    grid-template-columns: 80px repeat(7, minmax(0, 1fr));
    align-items: start;
  }

  .time-rail {
    position: relative;
    border-right: 1px solid #dfe7f3;
    background: #fbfcff;
  }

  .time-label {
    position: absolute;
    left: 0;
    width: 100%;
    padding-right: 0.85rem;
    color: #8a96a8;
    font-size: 0.72rem;
    font-weight: 700;
    text-align: right;
    line-height: 1;
    transform: translateY(-50%);
  }

  .week-day-column {
    position: relative;
    border-right: 1px solid #edf2f8;
    cursor: pointer;
    background-color: #ffffff;
    background-image: repeating-linear-gradient(
      to bottom,
      transparent 0,
      transparent ${HOUR_ROW_HEIGHT / 2 - 1}px,
      #f0f4fa ${HOUR_ROW_HEIGHT / 2 - 1}px,
      #f0f4fa ${HOUR_ROW_HEIGHT / 2}px,
      transparent ${HOUR_ROW_HEIGHT / 2}px,
      transparent ${HOUR_ROW_HEIGHT - 1}px,
      #e7edf6 ${HOUR_ROW_HEIGHT - 1}px,
      #e7edf6 ${HOUR_ROW_HEIGHT}px
    );
    background-position-y: ${WEEK_GRID_TOP_OFFSET}px;
  }

  .week-day-column:last-child {
    border-right: none;
  }

  .week-day-column.today {
    background-color: #f8fbff;
  }

  .week-day-column.selected {
    background-color: #f9fbff;
    box-shadow: inset 2px 0 0 #4078dc;
  }

  .week-day-column.muted {
    background-color: #fbfcfe;
  }

  .slot-add-target {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    border: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
    transition:
      background 0.12s ease,
      box-shadow 0.12s ease;
  }

  .slot-add-target:hover,
  .slot-add-target:focus-visible {
    background: rgba(64, 120, 220, 0.08);
    box-shadow: inset 0 0 0 1px rgba(64, 120, 220, 0.2);
    outline: none;
  }

  .time-event {
    position: absolute;
    left: 0;
    right: 0;
    box-sizing: border-box;
    border: 1px solid rgba(64, 120, 220, 0.18);
    border-left: 4px solid #4078dc;
    border-radius: 5px;
    padding: 0.28rem 0.45rem;
    text-align: left;
    background: #ffffff;
    color: #2457b8;
    box-shadow: 0 4px 10px rgba(34, 46, 84, 0.1);
    overflow: hidden;
  }

  .time-event.success {
    border-color: rgba(2, 122, 72, 0.18);
    border-left-color: #10b981;
    background: #ffffff;
    color: #027a48;
  }

  .time-event.attention {
    border-color: rgba(146, 64, 14, 0.2);
    border-left-color: #f1b44c;
    background: #ffffff;
    color: #92400e;
  }

  .time-event.failed {
    border-color: rgba(180, 35, 24, 0.22);
    border-left-color: #b42318;
    background: #fffafa;
    color: #b42318;
    box-shadow: 0 5px 14px rgba(180, 35, 24, 0.16);
  }

  .time-event.blackout {
    border-color: rgba(15, 23, 42, 0.3);
    border-left-color: #0f172a;
    background: #e2e8f0;
    color: #0f172a;
  }

  .time-event-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.35rem;
    height: 100%;
  }

  .time-event-title {
    min-width: 0;
    font-size: 0.74rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .time-event-time {
    flex: 0 0 auto;
    border-radius: 999px;
    padding: 0.1rem 0.34rem;
    background: #f3f6fb;
    font-size: 0.62rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }

  .time-event-warning {
    display: inline-flex;
    align-items: center;
    gap: 0.22rem;
    border-radius: 999px;
    padding: 0.08rem 0.3rem;
    background: rgba(180, 35, 24, 0.1);
    color: #b42318;
    font-size: 0.58rem;
    font-weight: 800;
    line-height: 1.2;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .time-event-subtitle {
    display: none;
    margin-top: 0.28rem;
    font-size: 0.72rem;
    line-height: 1.25;
    opacity: 0.94;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .current-time-line {
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    background: #ef4444;
    z-index: 8;
    pointer-events: none;
  }

  .current-time-line::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ef4444;
    transform: translateY(-50%);
  }

  .agenda-panel {
    min-height: 0;
    align-self: start;
    border-left: 1px solid #dfe7f3;
    background: #fbfcff;
  }

  .agenda-header {
    padding: 1rem;
    border-bottom: 1px solid #dfe7f3;
  }

  .agenda-title {
    margin: 0;
    color: #293241;
    font-size: 0.98rem;
  }

  .agenda-subtitle {
    margin: 0.3rem 0 0;
    color: #687386;
    font-size: 0.82rem;
    line-height: 1.4;
  }

  .agenda-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
  }

  .agenda-empty {
    padding: 1rem;
    color: #94a3b8;
    font-size: 0.84rem;
  }

  .agenda-entry {
    width: 100%;
    border: 1px solid #dfe7f3;
    border-radius: 7px;
    padding: 0.85rem 0.9rem;
    text-align: left;
    background: #ffffff;
    color: #293241;
    box-shadow: 0 4px 12px rgba(34, 46, 84, 0.05);
  }

  .agenda-entry.active {
    border-color: rgba(64, 120, 220, 0.28);
    background: #edf5ff;
  }

  .agenda-entry.success {
    border-color: #a7f3d0;
    background: #f3fbf6;
  }

  .agenda-entry.attention {
    border-color: #fcd34d;
    background: #fffbeb;
  }

  .agenda-entry.failed {
    border-color: #fda29b;
    background: #fff5f5;
    box-shadow: 0 4px 14px rgba(180, 35, 24, 0.1);
  }

  .agenda-entry.blackout {
    border-color: #94a3b8;
    background: #e2e8f0;
    color: #0f172a;
  }

  .agenda-entry-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .agenda-entry-title {
    font-size: 0.88rem;
    font-weight: 700;
  }

  .agenda-entry-kind {
    color: #64748b;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .agenda-entry.failed .agenda-entry-kind {
    color: #b42318;
  }

  .agenda-entry.active .agenda-entry-kind {
    color: #2457b8;
  }

  .agenda-entry.blackout .agenda-entry-kind {
    color: #0f172a;
  }

  .agenda-entry-subtitle {
    display: block;
    margin-top: 0.35rem;
    color: #64748b;
    font-size: 0.8rem;
    line-height: 1.4;
  }

  .agenda-entry-time {
    display: block;
    margin-top: 0.28rem;
    color: #475569;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .agenda-entry-estimate {
    display: block;
    margin-top: 0.35rem;
    color: #2457b8;
    font-size: 0.92rem;
    font-weight: 700;
  }

  @media (max-width: 1200px) {
    .calendar-title-top {
      flex-direction: column;
      align-items: flex-start;
    }

    .calendar-body {
      grid-template-columns: 1fr;
    }

    .agenda-panel {
      border-left: none;
      border-top: 1px solid #dfe7f3;
    }
  }

  @media (max-width: 900px) {
    .calendar-grid {
      grid-template-columns: 1fr;
    }

    .weekday-cell {
      display: none;
    }

    .day-cell {
      min-height: 0;
      border-right: none;
    }

    .week-header-row,
    .week-grid {
      grid-template-columns: 64px repeat(7, minmax(160px, 1fr));
      min-width: 1120px;
    }

    .week-scroll {
      overflow-x: auto;
    }
  }
`

const monthRange = (visibleMonth: Date) => {
  const monthStart = startOfMonth(visibleMonth)
  const monthEnd = endOfMonth(visibleMonth)
  return { monthStart, monthEnd }
}

const calendarGridDays = (visibleMonth: Date) => {
  const { monthStart, monthEnd } = monthRange(visibleMonth)
  const days: Date[] = []
  let cursor = monthStart
  while (cursor <= monthEnd) {
    days.push(cursor)
    cursor = addDays(cursor, 1)
  }
  return days
}

const weekRange = (visibleDate: Date) => ({
  weekStart: startOfWeek(visibleDate, { weekStartsOn: 1 }),
  weekEnd: endOfWeek(visibleDate, { weekStartsOn: 1 }),
})

const calendarWeekDays = (visibleDate: Date) => {
  const { weekStart, weekEnd } = weekRange(visibleDate)
  const days: Date[] = []
  let cursor = weekStart
  while (cursor <= weekEnd) {
    days.push(cursor)
    cursor = addDays(cursor, 1)
  }
  return days
}

const toInstantDateKey = (date: Date, timezone?: string) =>
  timezone ? moment(date).tz(timezone).format('YYYY-MM-DD') : format(date, 'yyyy-MM-dd')

const toDateKey = (date: Date, timezone?: string) => {
  if (!timezone) return format(date, 'yyyy-MM-dd')
  return moment
    .tz(
      {
        year: date.getFullYear(),
        month: date.getMonth(),
        day: date.getDate(),
        hour: 12,
      },
      timezone
    )
    .format('YYYY-MM-DD')
}

const getCalendarEntriesForDay = (entries: ScheduleCalendarEntry[], date: Date, timezone?: string) =>
  entries.filter(({ date: entryDate }) => entryDate === toDateKey(date, timezone))

const getMonthHeading = (visibleMonth: Date) => format(visibleMonth, 'MMMM yyyy')

const getWeekHeading = (visibleDate: Date) => {
  const { weekStart, weekEnd } = weekRange(visibleDate)
  return `${format(weekStart, 'MMM d')} - ${format(weekEnd, weekStart.getMonth() === weekEnd.getMonth() ? 'd, yyyy' : 'MMM d, yyyy')}`
}

const previousMonth = (visibleMonth: Date) => subMonths(visibleMonth, 1)

const nextMonth = (visibleMonth: Date) => addMonths(visibleMonth, 1)

const previousWeek = (visibleDate: Date) => subWeeks(visibleDate, 1)

const nextWeek = (visibleDate: Date) => addWeeks(visibleDate, 1)

const dayCellMuted = (visibleMonth: Date, day: Date) => !isSameMonth(day, visibleMonth)

type PositionedEntry = {
  entry: ScheduleCalendarEntry
  top: number
  height: number
  lane: number
  laneCount: number
}

type ScheduleCalendarProps = {
  getEntries: (rangeStart: Date, rangeEnd: Date) => ScheduleCalendarEntry[]
  visibleDate: Date
  setVisibleDate: (date: Date) => void
  viewMode: ScheduleCalendarViewMode
  setViewMode: (mode: ScheduleCalendarViewMode) => void
  onSelectEntry: (sourceId: number | string, entry: ScheduleCalendarEntry) => void
  canMutate?: boolean
  onCreateSlot?: (slot: ScheduleCalendarSlot) => void
  weekTitle?: string
  monthTitle?: string
  weekDescription?: string
  monthDescription?: string
  allowFullscreen?: boolean
  timezoneMode?: ScheduleCalendarTimezoneMode
  onTimezoneModeChange?: (mode: ScheduleCalendarTimezoneMode) => void
  warehouseTimezoneLabel?: string
  viewerTimezoneLabel?: string
  dayKeyTimezone?: string
  todayDateKey?: string
  currentMinutesInDay?: number | null
  eventDurationMinutes?: number
  createSlotIntervalMinutes?: number
}

const sameDay = (left: Date, right: Date) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()

const toneLabel = (entry: ScheduleCalendarEntry) => {
  if (entry.kind === 'blackout') return 'Blackout'
  if (entry.kind === 'manual_skip') return 'Manual Skip'
  if (entry.kind === 'running') return 'Running'
  if (entry.statusTone === 'failed') return 'Failed'
  if (entry.statusTone === 'success') return 'Completed'
  if (entry.statusTone === 'attention') return 'Needs Attention'
  return 'Upcoming'
}

const formatEntryTime = (entry: ScheduleCalendarEntry) =>
  entry.displayTimeLabel ||
  new Date(entry.scheduledAt).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })

const formatHourLabel = (hour: number) =>
  new Date(2026, 0, 1, hour, 0, 0).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })

const formatSlotLabel = (hour: number, minute = 0) =>
  new Date(2026, 0, 1, hour, minute, 0).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  })

const formatTimeValue = (hour: number, minute = 0) =>
  `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`

const buildSlot = (day: Date, hour: number, minute = 0, timezone?: string): ScheduleCalendarSlot => {
  const date = toDateKey(day, timezone)
  const time = formatTimeValue(hour, minute)
  const scheduledAt = timezone
    ? moment.tz(`${date} ${time}`, 'YYYY-MM-DD HH:mm:ss', timezone).toISOString()
    : new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute, 0, 0).toISOString()

  return {
    date,
    time,
    scheduledAt,
  }
}

const buildCreatableSlots = (startHour: number, endHour: number, slotIntervalMinutes: number) =>
  Array.from({ length: Math.max(0, (endHour - startHour) * (60 / slotIntervalMinutes)) }, (_, index) => {
    const minutesFromStart = index * slotIntervalMinutes
    return {
      hour: startHour + Math.floor(minutesFromStart / 60),
      minute: minutesFromStart % 60,
      top: WEEK_GRID_TOP_OFFSET + (minutesFromStart / 60) * HOUR_ROW_HEIGHT,
      height: Math.max((slotIntervalMinutes / 60) * HOUR_ROW_HEIGHT, MIN_EVENT_CARD_HEIGHT),
    }
  })

const deriveHourRange = () => ({
  startHour: DEFAULT_START_HOUR,
  endHour: DEFAULT_END_HOUR,
})

const buildPositionedEntries = (
  dayEntries: ScheduleCalendarEntry[],
  startHour: number,
  eventDurationMinutes: number
): PositionedEntry[] => {
  const eventHeight = Math.max((eventDurationMinutes / 60) * HOUR_ROW_HEIGHT, MIN_EVENT_CARD_HEIGHT)
  const sortedEntries = dayEntries
    .filter((entry) => entry.minutesFromMidnight != null)
    .slice()
    .sort((left, right) => left.scheduledAt.localeCompare(right.scheduledAt))

  const positioned: PositionedEntry[] = []
  let cluster: ScheduleCalendarEntry[] = []
  let clusterEndMinutes = -1

  const flushCluster = () => {
    if (!cluster.length) return
    const laneEndByIndex: number[] = []
    const clusterPositioned: PositionedEntry[] = []

    cluster.forEach((entry) => {
      const entryStartMinutes = entry.minutesFromMidnight || 0
      let laneIndex = laneEndByIndex.findIndex((laneEnd) => entryStartMinutes >= laneEnd)
      if (laneIndex === -1) {
        laneIndex = laneEndByIndex.length
        laneEndByIndex.push(entryStartMinutes + eventDurationMinutes)
      } else {
        laneEndByIndex[laneIndex] = entryStartMinutes + eventDurationMinutes
      }

      const minutesFromStart = entryStartMinutes - startHour * 60
      clusterPositioned.push({
        entry,
        top: WEEK_GRID_TOP_OFFSET + (minutesFromStart / 60) * HOUR_ROW_HEIGHT,
        height: eventHeight,
        lane: laneIndex,
        laneCount: 1,
      })
    })

    const clusterLaneCount = laneEndByIndex.length
    clusterPositioned.forEach((item) => {
      item.laneCount = clusterLaneCount
    })
    positioned.push(...clusterPositioned)
    cluster = []
    clusterEndMinutes = -1
  }

  sortedEntries.forEach((entry) => {
    const entryStartMinutes = entry.minutesFromMidnight || 0
    if (!cluster.length) {
      cluster = [entry]
      clusterEndMinutes = entryStartMinutes + eventDurationMinutes
      return
    }

    if (entryStartMinutes >= clusterEndMinutes) {
      flushCluster()
      cluster = [entry]
      clusterEndMinutes = entryStartMinutes + eventDurationMinutes
      return
    }

    cluster.push(entry)
    clusterEndMinutes = Math.max(clusterEndMinutes, entryStartMinutes + eventDurationMinutes)
  })

  flushCluster()

  return positioned.sort((left, right) => left.entry.scheduledAt.localeCompare(right.entry.scheduledAt))
}

export const ScheduleCalendar = ({
  getEntries,
  visibleDate,
  setVisibleDate,
  viewMode,
  setViewMode,
  onSelectEntry,
  canMutate = false,
  onCreateSlot,
  weekTitle = '',
  monthTitle = '',
  weekDescription = '',
  monthDescription = '',
  allowFullscreen = false,
  timezoneMode,
  onTimezoneModeChange,
  warehouseTimezoneLabel,
  viewerTimezoneLabel,
  dayKeyTimezone,
  todayDateKey,
  currentMinutesInDay = null,
  eventDurationMinutes = DEFAULT_EVENT_DURATION_MINUTES,
  createSlotIntervalMinutes = DEFAULT_CREATE_SLOT_INTERVAL_MINUTES,
}: ScheduleCalendarProps) => {
  const [today, setToday] = useState(() => new Date())
  const weekScrollRef = useRef<HTMLDivElement | null>(null)
  const initialWeekScrollAppliedRef = useRef(false)
  const { weekStart, weekEnd } = weekRange(visibleDate)
  const { monthStart, monthEnd } = monthRange(visibleDate)
  const days = viewMode === 'month' ? calendarGridDays(visibleDate) : calendarWeekDays(visibleDate)
  const entries = useMemo(
    () => getEntries(viewMode === 'month' ? monthStart : weekStart, viewMode === 'month' ? monthEnd : weekEnd),
    [getEntries, monthEnd, monthStart, viewMode, weekEnd, weekStart]
  )
  const heading = viewMode === 'month' ? getMonthHeading(visibleDate) : getWeekHeading(visibleDate)
  const title = viewMode === 'week' ? weekTitle || heading : monthTitle || heading
  const copy = viewMode === 'week' ? weekDescription : monthDescription
  const monthLeadingEmptyCells = viewMode === 'month' && days.length ? (days[0].getDay() + 6) % 7 : 0

  const [selectedDate, setSelectedDate] = useState<Date>(today)
  const [fullscreen, setFullscreen] = useState(false)
  const effectiveTodayDateKey = todayDateKey || toInstantDateKey(today, dayKeyTimezone)

  useEffect(() => {
    if (!fullscreen) return

    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
    }
  }, [fullscreen])

  useEffect(() => {
    const intervalId = window.setInterval(() => setToday(new Date()), 60_000)
    return () => window.clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const inRange = days.some((day) => sameDay(day, selectedDate))
    if (inRange) return
    const todayInRange = days.find((day) => toDateKey(day, dayKeyTimezone) === effectiveTodayDateKey)
    setSelectedDate(todayInRange || days[0])
  }, [dayKeyTimezone, days, effectiveTodayDateKey, selectedDate])

  const selectedEntries = useMemo(
    () => getCalendarEntriesForDay(entries, selectedDate, dayKeyTimezone),
    [dayKeyTimezone, entries, selectedDate]
  )
  const { startHour, endHour } = useMemo(() => deriveHourRange(), [])
  const gridHeight = (endHour - startHour) * HOUR_ROW_HEIGHT
  const gridContentHeight = gridHeight + WEEK_GRID_TOP_OFFSET + WEEK_GRID_BOTTOM_OFFSET
  const timeLabels = useMemo(
    () => Array.from({ length: endHour - startHour }, (_, index) => startHour + index),
    [endHour, startHour]
  )
  const creatableSlots = useMemo(
    () => buildCreatableSlots(startHour, endHour, createSlotIntervalMinutes),
    [createSlotIntervalMinutes, endHour, startHour]
  )
  useEffect(() => {
    if (viewMode !== 'week' || !weekScrollRef.current) return
    if (initialWeekScrollAppliedRef.current) return
    weekScrollRef.current.scrollTop = WEEK_GRID_TOP_OFFSET + (INITIAL_WEEK_SCROLL_HOUR - startHour) * HOUR_ROW_HEIGHT
    initialWeekScrollAppliedRef.current = true
  }, [startHour, viewMode])

  const positionedEntriesByDay = useMemo(() => {
    const positioned = new Map<string, PositionedEntry[]>()
    days.forEach((day) => {
      const dayEntries = getCalendarEntriesForDay(entries, day, dayKeyTimezone)
      positioned.set(day.toDateString(), buildPositionedEntries(dayEntries, startHour, eventDurationMinutes))
    })
    return positioned
  }, [dayKeyTimezone, days, entries, eventDurationMinutes, startHour])

  const nowTop = useMemo(() => {
    if (currentMinutesInDay == null) return null
    if (viewMode !== 'week' || !days.some((day) => toDateKey(day, dayKeyTimezone) === effectiveTodayDateKey))
      return null
    const currentHour = currentMinutesInDay / 60
    if (currentHour < startHour || currentHour > endHour) return null
    return WEEK_GRID_TOP_OFFSET + ((currentHour - startHour) / (endHour - startHour)) * gridHeight
  }, [currentMinutesInDay, dayKeyTimezone, days, effectiveTodayDateKey, endHour, gridHeight, startHour, viewMode])

  const goPrevious = () => setVisibleDate(viewMode === 'month' ? previousMonth(visibleDate) : previousWeek(visibleDate))
  const goNext = () => setVisibleDate(viewMode === 'month' ? nextMonth(visibleDate) : nextWeek(visibleDate))
  const goToday = () => {
    const currentDate = new Date()
    setToday(currentDate)
    const [year, month, day] = effectiveTodayDateKey.split('-').map(Number)
    const displayToday = new Date(year, month - 1, day)
    setSelectedDate(displayToday)
    setVisibleDate(displayToday)
  }

  const createSlot = (day: Date, hour: number, minute = 0) => {
    if (!canMutate || !onCreateSlot) return
    setSelectedDate(day)
    onCreateSlot(buildSlot(day, hour, minute, dayKeyTimezone))
  }

  return (
    <CalendarPanel className={fullscreen ? 'fullscreen' : ''}>
      <div className="calendar-header">
        <div className="calendar-title">
          <div className="calendar-title-top">
            <h4>{title}</h4>
            {timezoneMode && onTimezoneModeChange && warehouseTimezoneLabel && viewerTimezoneLabel ? (
              <div className="timezone-switch" role="group" aria-label="Time display timezone">
                <button
                  className={timezoneMode === 'warehouse' ? 'active' : ''}
                  type="button"
                  onClick={() => onTimezoneModeChange('warehouse')}
                >
                  Warehouse ({warehouseTimezoneLabel})
                </button>
                <button
                  className={timezoneMode === 'viewer' ? 'active' : ''}
                  type="button"
                  onClick={() => onTimezoneModeChange('viewer')}
                >
                  My time ({viewerTimezoneLabel})
                </button>
              </div>
            ) : null}
          </div>
          {copy ? <p>{copy}</p> : null}
        </div>
        <div className="calendar-controls">
          <div className="view-toggle">
            <button className={viewMode === 'week' ? 'active' : ''} type="button" onClick={() => setViewMode('week')}>
              Week
            </button>
            <button className={viewMode === 'month' ? 'active' : ''} type="button" onClick={() => setViewMode('month')}>
              Month
            </button>
          </div>
          <div className="calendar-nav">
            <button className="calendar-button calendar-today-button" type="button" onClick={goToday}>
              Today
            </button>
            <button
              className="calendar-button"
              type="button"
              aria-label={viewMode === 'month' ? 'Previous month' : 'Previous week'}
              onClick={goPrevious}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <strong>{heading}</strong>
            <button
              className="calendar-button"
              type="button"
              aria-label={viewMode === 'month' ? 'Next month' : 'Next week'}
              onClick={goNext}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          {allowFullscreen ? (
            <button
              className="calendar-button calendar-expand-button"
              type="button"
              aria-label={fullscreen ? 'Exit full screen calendar' : 'Open full screen calendar'}
              title={fullscreen ? 'Exit full screen' : 'Full screen'}
              onClick={() => setFullscreen((current) => !current)}
            >
              <FontAwesomeIcon icon={fullscreen ? faCompressArrowsAlt : faExpandArrowsAlt} />
            </button>
          ) : null}
        </div>
      </div>
      <div className="calendar-body">
        <div className="calendar-main">
          {viewMode === 'week' ? (
            <div className="week-shell">
              <div className="week-scroll" ref={weekScrollRef}>
                <div className="week-header-row">
                  <div className="week-corner" />
                  {days.map((day) => {
                    const isToday = toDateKey(day, dayKeyTimezone) === effectiveTodayDateKey
                    const isSelected = sameDay(day, selectedDate)
                    return (
                      <div
                        className={`week-header-cell${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                        key={day.toISOString()}
                        onClick={() => setSelectedDate(day)}
                      >
                        <span className="week-header-label">{weekdayOptions[(day.getDay() + 6) % 7].shortLabel}</span>
                        <span className="week-header-date">{day.getDate()}</span>
                      </div>
                    )
                  })}
                </div>
                <div className="week-grid">
                  <div className="time-rail" style={{ height: gridContentHeight }}>
                    {timeLabels.map((hour) => (
                      <div
                        className="time-label"
                        key={hour}
                        style={{ top: WEEK_GRID_TOP_OFFSET + (hour - startHour) * HOUR_ROW_HEIGHT }}
                      >
                        {formatHourLabel(hour)}
                      </div>
                    ))}
                  </div>
                  {days.map((day) => {
                    const isToday = toDateKey(day, dayKeyTimezone) === effectiveTodayDateKey
                    const isSelected = sameDay(day, selectedDate)
                    const positionedEntries = positionedEntriesByDay.get(day.toDateString()) || []
                    return (
                      <div
                        className={`week-day-column${dayCellMuted(visibleDate, day) ? ' muted' : ''}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                        key={day.toISOString()}
                        style={{ height: gridContentHeight }}
                        onClick={() => setSelectedDate(day)}
                      >
                        {isToday && nowTop != null ? (
                          <div className="current-time-line" style={{ top: nowTop }} />
                        ) : null}
                        {canMutate && onCreateSlot
                          ? creatableSlots.map(({ hour, minute, top, height }) => (
                              <button
                                aria-label={`Create schedule at ${formatSlotLabel(hour, minute)}`}
                                className="slot-add-target"
                                key={`${hour}-${minute}`}
                                type="button"
                                style={{ top, height }}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  createSlot(day, hour, minute)
                                }}
                              />
                            ))
                          : null}
                        {positionedEntries.map(({ entry, top, height, lane, laneCount }) => {
                          const laneGap = laneCount > 1 ? 3 : 0
                          const laneWidth = 100 / laneCount
                          return (
                            <button
                              className={`time-event ${entry.statusTone}`}
                              key={entry.id}
                              type="button"
                              style={{
                                top,
                                height,
                                left: `calc(${lane * laneWidth}% + ${lane ? laneGap / 2 : 0}px)`,
                                right: `calc(${100 - (lane + 1) * laneWidth}% + ${
                                  lane < laneCount - 1 ? laneGap / 2 : 0
                                }px)`,
                                zIndex: 2,
                              }}
                              onClick={(event) => {
                                event.stopPropagation()
                                setSelectedDate(day)
                                onSelectEntry(entry.sourceId, entry)
                              }}
                            >
                              <div className="time-event-topline">
                                <span className="time-event-title">{entry.title}</span>
                                {entry.statusTone === 'failed' ? (
                                  <span className="time-event-warning">
                                    <FontAwesomeIcon icon={faExclamationTriangle} /> Failed
                                  </span>
                                ) : (
                                  <span className="time-event-time">{formatEntryTime(entry)}</span>
                                )}
                              </div>
                              <span className="time-event-subtitle">{entry.subtitle}</span>
                            </button>
                          )
                        })}
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="calendar-grid">
              {weekdayOptions.map(({ shortLabel }) => (
                <div className="weekday-cell" key={shortLabel}>
                  {shortLabel}
                </div>
              ))}
              {Array.from({ length: monthLeadingEmptyCells }).map((_, index) => (
                <div aria-hidden className="day-cell placeholder" key={`placeholder-${index}`} />
              ))}
              {days.map((day, index) => {
                const dayEntries = getCalendarEntriesForDay(entries, day, dayKeyTimezone)
                const visibleEntries = dayEntries.slice(0, 3)
                const isToday = toDateKey(day, dayKeyTimezone) === effectiveTodayDateKey
                const isSelected = sameDay(day, selectedDate)

                return (
                  <div
                    className={`day-cell${dayCellMuted(visibleDate, day) ? ' muted' : ''}${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                    key={day.toISOString()}
                    onClick={() => setSelectedDate(day)}
                  >
                    <div className="day-number-row">
                      <div className="day-number">
                        {weekdayOptions[(day.getDay() + 6) % 7].shortLabel} {day.getDate()}
                      </div>
                      {isToday ? <span className="today-badge">Today</span> : null}
                    </div>
                    <div className="entry-list">
                      {visibleEntries.map((entry) => (
                        <button
                          className={`entry ${entry.statusTone}`}
                          key={entry.id}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            setSelectedDate(day)
                            onSelectEntry(entry.sourceId, entry)
                          }}
                        >
                          <div className="entry-topline">
                            <span className="entry-title">{entry.title}</span>
                            <span className="entry-time">{formatEntryTime(entry)}</span>
                          </div>
                          {entry.statusTone === 'failed' ? (
                            <span className="entry-warning">
                              <FontAwesomeIcon icon={faExclamationTriangle} /> Failed
                            </span>
                          ) : null}
                          <span className="entry-subtitle">{entry.subtitle}</span>
                        </button>
                      ))}
                      {dayEntries.length > visibleEntries.length ? (
                        <span className="entry-more">+{dayEntries.length - visibleEntries.length} more</span>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        <aside className="agenda-panel">
          <div className="agenda-header">
            <h4 className="agenda-title">
              Agenda for{' '}
              {selectedDate.toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </h4>
            <p className="agenda-subtitle">
              {sameDay(selectedDate, today) && effectiveTodayDateKey === toDateKey(today, dayKeyTimezone)
                ? 'Today’s planned runs and completed slots.'
                : 'Click another day in the calendar to inspect that day’s planned runs and outcomes.'}
            </p>
          </div>
          {selectedEntries.length ? (
            <div className="agenda-list">
              {selectedEntries.map((entry) => (
                <button
                  className={`agenda-entry ${entry.statusTone}`}
                  key={entry.id}
                  type="button"
                  onClick={() => onSelectEntry(entry.sourceId, entry)}
                >
                  <div className="agenda-entry-topline">
                    <span className="agenda-entry-title">{entry.title}</span>
                    <span className="agenda-entry-kind">
                      {entry.statusTone === 'failed' ? <FontAwesomeIcon icon={faExclamationTriangle} /> : null}{' '}
                      {toneLabel(entry)}
                    </span>
                  </div>
                  <span className="agenda-entry-time">{formatEntryTime(entry)}</span>
                  {entry.orderEstimateLabel ? (
                    <span className="agenda-entry-estimate">{entry.orderEstimateLabel}</span>
                  ) : null}
                  <span className="agenda-entry-subtitle">{entry.subtitle}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="agenda-empty">No launches or blackout dates are scheduled for this day.</div>
          )}
        </aside>
      </div>
    </CalendarPanel>
  )
}
